/*
 * Magnetic snapping.
 *
 * liveWire  "intelligent scissors" (Mortensen & Barrett, 1995): the cheapest
 *           8-connected path between two points, where crossing a strong edge
 *           is cheap and crossing flat texture is expensive, so the path hugs
 *           the cap/stem boundary. Dijkstra with a binary heap, confined to the
 *           two points' bounding box plus a margin.
 * snapPoint the keyboard and joystick version: move a single point to the best
 *           edge pixel nearby, preferring the offset used last time so the cursor
 *           stays on the same edge instead of zig-zagging between two.
 * Coordinates are analysis-image pixels.
 */
// Pass analyseImage's sharp `edge` gradient. The fourth power makes anything short
// of a strong edge expensive, so the wire keeps to the edge around corners.
export function costFromGradient(grad) {
  const cost = new Float32Array(grad.length);
  for (let i = 0; i < grad.length; i += 1) cost[i] = 0.02 + (1 - grad[i]) ** 4;
  return cost;
}

class MinHeap {
  constructor() {
    this.ids = [];
    this.keys = [];
  }

  get size() {
    return this.ids.length;
  }

  push(id, key) {
    const { ids, keys } = this;
    let i = ids.length;
    ids.push(id);
    keys.push(key);
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (keys[parent] <= keys[i]) break;
      [ids[i], ids[parent]] = [ids[parent], ids[i]];
      [keys[i], keys[parent]] = [keys[parent], keys[i]];
      i = parent;
    }
  }

  pop() {
    const { ids, keys } = this;
    const top = [ids[0], keys[0]];
    const lastId = ids.pop();
    const lastKey = keys.pop();
    if (ids.length) {
      ids[0] = lastId;
      keys[0] = lastKey;
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        const r = l + 1;
        let m = i;
        if (l < ids.length && keys[l] < keys[m]) m = l;
        if (r < ids.length && keys[r] < keys[m]) m = r;
        if (m === i) break;
        [ids[i], ids[m]] = [ids[m], ids[i]];
        [keys[i], keys[m]] = [keys[m], keys[i]];
        i = m;
      }
    }
    return top;
  }
}

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

export function liveWire(cost, w, h, from, to, margin = 40) {
  const fx = clamp(Math.floor(from[0]), 0, w - 1);
  const fy = clamp(Math.floor(from[1]), 0, h - 1);
  const tx = clamp(Math.floor(to[0]), 0, w - 1);
  const ty = clamp(Math.floor(to[1]), 0, h - 1);
  const x0 = clamp(Math.min(fx, tx) - margin, 0, w - 1);
  const x1 = clamp(Math.max(fx, tx) + margin, 0, w - 1);
  const y0 = clamp(Math.min(fy, ty) - margin, 0, h - 1);
  const y1 = clamp(Math.max(fy, ty) + margin, 0, h - 1);
  const bw = x1 - x0 + 1;
  const bh = y1 - y0 + 1;
  // Float64, not Float32: a distance rounded down on storage compares below the
  // exact key in the heap, the node is skipped as stale, and the target is never reached.
  const dist = new Float64Array(bw * bh).fill(Infinity);
  const prev = new Int32Array(bw * bh).fill(-1);
  const start = (fy - y0) * bw + (fx - x0);
  const target = (ty - y0) * bw + (tx - x0);
  const heap = new MinHeap();
  dist[start] = 0;
  heap.push(start, 0);
  while (heap.size) {
    const [i, d] = heap.pop();
    if (d > dist[i]) continue;
    if (i === target) break;
    const lx = i % bw;
    const ly = (i - lx) / bw;
    for (let dy = -1; dy <= 1; dy += 1) {
      for (let dx = -1; dx <= 1; dx += 1) {
        if (!dx && !dy) continue;
        const nx = lx + dx;
        const ny = ly + dy;
        if (nx < 0 || ny < 0 || nx >= bw || ny >= bh) continue;
        const n = ny * bw + nx;
        const step = cost[(ny + y0) * w + (nx + x0)] * (dx && dy ? Math.SQRT2 : 1);
        if (d + step < dist[n]) {
          dist[n] = d + step;
          prev[n] = i;
          heap.push(n, d + step);
        }
      }
    }
  }
  if (target !== start && prev[target] === -1) return [from, to];
  const path = [];
  for (let i = target; i !== -1; i = prev[i]) {
    const lx = i % bw;
    path.push([lx + x0 + 0.5, (i - lx) / bw + y0 + 0.5]);
    if (i === start) break;
  }
  return path.reverse();
}

export function snapPoint(analysis, point, { radius = 6, previousOffset = null, distanceWeight = 0.015, offsetWeight = 0.02 } = {}) {
  const { width: w, height: h, grad, threshold } = analysis;
  const px = Math.floor(point[0]);
  const py = Math.floor(point[1]);
  let best = null;
  let bestScore = -Infinity;
  for (let dy = -radius; dy <= radius; dy += 1) {
    for (let dx = -radius; dx <= radius; dx += 1) {
      if (dx * dx + dy * dy > radius * radius) continue;
      const x = px + dx;
      const y = py + dy;
      if (x < 0 || y < 0 || x >= w || y >= h) continue;
      const g = grad[y * w + x];
      if (g < threshold) continue;
      const score = g - distanceWeight * Math.hypot(dx, dy)
        - (previousOffset ? offsetWeight * Math.hypot(dx - previousOffset[0], dy - previousOffset[1]) : 0);
      if (score > bestScore) {
        bestScore = score;
        best = [dx, dy];
      }
    }
  }
  if (!best) return { point, offset: null };
  return { point: [px + best[0] + 0.5, py + best[1] + 0.5], offset: best };
}
